<script lang="ts" setup>
import Modal from '~/components/Modal.vue'
import Button from '~/components/Button.vue'
import InputAmount from '~/components/InputAmount.vue'
import PaymentMethodLogo from '~/components/Payment/PaymentMethodLogo.vue'
import { useBalanceStore } from '~/store/balance'
import StripeElement from '~/components/StripeElement.vue'
import { useStoreConfig } from '~/store/storeConfig'
import getAvailableCountries from '~/utils/balance/getAvailableCountries'
import toRawPrice from '~/utils/toRawPrice'
import toPrice from '~/utils/toPrice'
import getDefaultCountryId from '~/utils/countries/getDefaultCountryId'
import type { CountryItem } from '~/types/payment/paymentFrontedTypes'

const { t } = useLang()
const { isMobile } = useStoreConfig()
const balance = useBalanceStore()

const selectedCountry = ref<CountryItem | null>(null)
const defaultCountry = getDefaultCountryId()?.toString()

const formData = ref<{ provider_type: any; amount: any; country_id: any }>({
  amount: '',
  country_id: defaultCountry,
  provider_type: null,
})

onMounted(async () => {
  await balance.fetch()

  selectedCountry.value = balance.countries.find((country) => country.value === defaultCountry) ?? null
  formData.value.country_id = selectedCountry.value?.value ?? null
})

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const visible = ref<Boolean>(props.visible)

const countries = await getAvailableCountries()

watch(
  () => props.visible,
  (isVisible) => {
    visible.value = isVisible

    if (!isVisible) {
      balance.setSelected(null)
      formData.value = {
        amount: '',
        country_id: defaultCountry,
        provider_type: null,
      }
    }
  }
)

watch(
  () => ({
    amount: formData.value.amount,
    country_id: formData.value.country_id,
  }),
  async (data) => {
    if (parseFloat(data.amount) > 0 && data.country_id) {
      await balance.updatePaymentMethods(countries, toRawPrice(parseFloat(data.amount)), parseInt(data.country_id))
    } else {
      balance.setPaymentMethods([])
    }
    formData.value.provider_type = null
  },
  {
    immediate: true,
  }
)

watch(
  () => formData.value.provider_type,
  (type) => {
    if (type) {
      balance.setSelected(type)
    } else {
      balance.setSelected(null)
    }
  }
)
const emit = defineEmits(['handle'])

const exchangeRateAmount = computed(() => {
  return `$${toRawPrice(parseFloat(formData.value.amount as string))?.toFixed(2)}`
})

function handle(value: boolean) {
  emit('handle', value)
}

function handleDeposit() {
  pay().then(() => {})
}

async function pay() {
  const data = {
    amount: toRawPrice(parseFloat(formData.value.amount)),
    country_id: parseInt(formData.value.country_id),
    provider_type: parseInt(formData.value.provider_type) ?? null,
  }
  await balance.deposit(data)
}

function onDestinationClick(country: { value: string; label: string }) {
  formData.value.country_id = country.value
  selectedCountry.value = country
}

const items = computed(() => balance.countries.map((country) => ({ data: country })))
</script>

<template>
  <div>
    <Modal :visible="visible" :header="t('balance.top_up_the_balance')" size-md is-full-screen @handle="handle">
      <div class="modal-content overflow-y-auto">
        <FormKit type="form" :actions="false" @submit="handleDeposit">
          <template #messages />

          <template #default="{ disabled }">
            <div v-if="isMobile" class="font-bold text-lg py-3 mb-4">
              {{ t('balance.select_required_information') }}
            </div>
            <div class="balance-block flex flex-col md:flex-row md:justify-between mb-4 gap-y-5 gap-x-4">
              <InputAmount
                :value="formData.amount"
                required
                class="basis-1/2"
                :label="t('balance.deposit_amount')"
                :placeholder="t('balance.enter_amount')"
                @handle="(value) => (formData.amount = value)" />
              <div class="destination-select flex flex-col basis-1/2">
                <label class="formkit-label !font-normal !font-secondary !text-xs !pb-1">
                  <span>{{ t('balance.country') }}</span>
                  <span class="text-brand-primary text-sm"> *</span>
                </label>

                <Dropdown v-if="selectedCountry" :items="items" strategy="fixed">
                  <template #header>
                    <div
                      class="formkit-inner destination text-text2 dark:text-dmText2 bg-white dark:bg-dmGray1 flex justify-between w-full">
                      <span class="ml-2 text-text1 dark:text-dmText1 font-medium h-[17px] py-[2px]">
                        {{ selectedCountry.label }}
                      </span>
                      <i class="fas fa-caret-down mr-1" />
                    </div>
                  </template>

                  <template #dropdown-item="{ item: destination }">
                    <div
                      :key="destination.value"
                      :class="{
                        active: destination.value === formData.country_id,
                      }"
                      @click="onDestinationClick(destination)">
                      <div class="flex items-center gap-2">
                        {{ t(destination.label) }}
                      </div>
                    </div>
                  </template>
                </Dropdown>
              </div>
            </div>
            <div v-if="balance.paymentMethods.length">
              <Alert v-if="useStoreConfig().getSelectedCurrency.iso_code !== 'USD'" warning class="mb-4">
                <span>{{ `${t('balance.exchange_rate_message')} ` }}</span>
                <span class="font-bold text-brand-primary">{{ exchangeRateAmount }}</span>
              </Alert>
              <div class="balance-block mb-5">
                <div class="font-bold pb-4">{{ t('balance.select_payment_type') }}</div>
                <div class="payment-methods-component">
                  <div class="payment-methods-list">
                    <div
                      v-for="(paymentMethodOption, index) in balance.paymentMethodOptions"
                      :key="index"
                      class="payment-method-item"
                      :class="{ active: formData.provider_type === paymentMethodOption.value }">
                      <FormKit v-model="formData.provider_type" type="radio" :options="[paymentMethodOption]">
                        <template #label="context">
                          <div :id="context.option.attrs.id" class="payment-methods-label">
                            <PaymentMethodLogo :payment-method="context.option.data" :small="false" />
                            <div v-if="context.option.data.fee > 0" class="payment-method-note">
                              <span>
                                {{ t('balance.transaction_fee') }}&nbsp;{{ balance.formatFee(context.option.data) }}
                              </span>
                            </div>
                          </div>
                        </template>
                      </FormKit>
                    </div>
                  </div>
                  <template v-if="balance.isStripeSelected && balance.stripeEnabled">
                    <div class="block-separator-full" />
                    <client-only>
                      <StripeElement :payment-method="balance.stripeMethod" />
                    </client-only>
                  </template>
                </div>
              </div>
            </div>
            <div
              v-if="balance.selectedMethod && formData.amount"
              class="deposit-block flex items-center flex-wrap gap-x-4">
              <div class="flex basis-full pb-4 md:pb-0 md:basis-1/3 justify-between">
                <div class="flex items-center text-text1 dark:text-dmText1">
                  <span>{{ t('balance.total') }}:</span>
                </div>
                <h5>{{ toPrice(parseFloat(balance.selectedMethod.sum_to_pay)) }}</h5>
              </div>
              <Button submit class="basis-1/3 flex-grow" :disabled="disabled">
                {{ `${t('balance.deposit')} (${balance.formatPrice(formData.amount)})` }}
              </Button>
            </div>
          </template>
        </FormKit>
      </div>
    </Modal>
  </div>
</template>

<style lang="postcss" scoped>
:deep(.modal) {
  @apply max-h-screen;

  .modal-body {
    @apply h-full;
  }
}

.payment-methods-component {
  @apply flex flex-col;
  .payment-methods-list {
    @apply grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4;
    .payment-method-item {
      @apply border border-solid border-neutral6 dark:border-dmGray1 rounded-sm overflow-hidden;
      &.active {
        @apply border-brand-primary;
      }
    }
  }

  .block-separator-full {
    @apply w-full h-[1px] bg-gray2 my-2 xl:my-5;
  }
}

.destination-select {
  img {
    height: 15px;
    border-radius: 3px;
  }

  .destination {
    @apply rounded-[0.375rem] text-base lg:text-sm px-[0.5875rem] py-[0.5625rem] lg:py-[0.65625rem];

    span {
      @apply leading-none;
    }
  }

  :deep(.dropdown-wrapper) {
    .dropdown-body {
      @apply overflow-y-auto;
      max-height: 200px;
    }
  }
}

.balance-block {
  @apply p-5 bg-neutral7 dark:bg-dmGray2;

  flex: 0 1 auto;
  border-radius: theme('borderRadius.sm');
}

.deposit-block {
  @extend .balance-block bg-neutral8 dark:bg-dmgray1;
}
</style>
